/**
 * DOM 圆形覆盖物
 *
 * 层级：map-core/overlays/drawing
 *
 * 职责：
 * 1. 以 DOM div（border-radius 圆）渲染绘制期的圆点/圆环
 * 2. draw() 时通过 pointToOverlayPixel 定位，transform translate(-50%,-50%) 居中
 *
 * 为什么不用 BMapGL.Marker + Icon：
 * Marker 走 WebGL 渲染管线，图标纹理上传/重绘时机由 SDK 内部调度，
 * 实测在部分机器上圆点比点击慢一拍才出现（单击不画圆 / 圆圈停在上一点击位置），
 * DOM 覆盖物由浏览器直接合成，创建即渲染，不受 SDK 重绘调度影响。
 */
export interface DomCircleStyle {
  /** 圆的总尺寸（含边框），px */
  size: number;
  /** 填充色（透明填充传 rgba） */
  background: string;
  borderWidth: number;
  borderColor: string;
  boxShadow?: string;
  /** 附加 class，便于测试与调试定位 */
  className?: string;
}

/** 获取 BMapGL.Overlay 基类，未就绪时返回 null */
function getOverlayBase(): any | null {
  return (window as any)?.BMapGL?.Overlay ?? null;
}

export class DomCircleOverlay {
  private el: HTMLDivElement;
  private inner: any = null;
  private overlayMap: any = null;

  constructor(private point: any, style: DomCircleStyle) {
    const contentSize = style.size - style.borderWidth * 2;
    this.el = document.createElement('div');
    this.el.className = style.className ?? 'map-core-dom-circle';
    this.el.style.cssText = [
      `width:${contentSize}px`,
      `height:${contentSize}px`,
      `background:${style.background}`,
      `border:${style.borderWidth}px solid ${style.borderColor}`,
      'border-radius:50%',
      'box-sizing:content-box',
      'position:absolute',
      // 圆心对齐地理坐标点，替代 Marker Icon 的 anchor
      'transform:translate(-50%,-50%)',
      // 不拦截地图鼠标事件（原 Marker 的 enableClicking:false）
      'pointer-events:none',
      style.boxShadow ? `box-shadow:${style.boxShadow}` : '',
    ]
      .filter(Boolean)
      .join(';');
  }

  /** 挂载到地图，返回原生覆盖物实例（必须在 BMapGL 就绪后调用） */
  mount(map: any): any {
    const Base = getOverlayBase();
    if (!Base) {
      console.error('[DomCircleOverlay] BMapGL.Overlay is not available');
      return null;
    }

    const self = this;

    class InnerOverlay extends Base {
      initialize = (m: any) => {
        self.overlayMap = m;
        m.getPanes().floatPane.appendChild(self.el);
        return self.el;
      };

      draw = () => {
        if (!self.overlayMap) return;
        const pixel = self.overlayMap.pointToOverlayPixel(self.point);
        self.el.style.left = `${pixel.x}px`;
        self.el.style.top = `${pixel.y}px`;
      };
    }

    this.inner = new InnerOverlay();
    map.addOverlay(this.inner);
    return this.inner;
  }

  setPosition(nextPoint: any) {
    this.point = nextPoint;
    this.inner?.draw?.();
  }

  remove(map: any) {
    if (this.inner) {
      map.removeOverlay(this.inner);
      this.inner = null;
    }
    this.overlayMap = null;
  }
}
